import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthStore } from '@/app/store/auth.store';
import { toast } from '@/app/store/toast.store';
import { useOptionalConsent } from '@/shared/hooks/use-optional-consent';
import { useTerm } from '@/shared/hooks/use-terms';

import { TermBadge } from './TermBadge';

const formatIsoToDot = (iso: string): string =>
  iso.slice(0, 10).replace(/-/g, '.');

// Full-page terms viewer (prototype: terms-view.html). Entered from the settings
// list "보기 →" and the login/signup footer. `?typeId=<n>` selects the term;
// `?consent=1` enables the bottom [동의하기] button for an optional, not-yet-agreed
// term — but only for an authenticated user (opt-in is recorded server-side).
export function TermsViewPage() {
  const { t } = useTranslation('termsPolicies');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { agreed, agree } = useOptionalConsent();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const raw = searchParams.get('typeId');
  const typeId = raw && /^\d+$/.test(raw) ? Number(raw) : null;
  const consentMode = searchParams.get('consent') === '1';

  const { data: term, isLoading, isError, refetch } = useTerm(typeId);

  const goBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/settings');
  }, [navigate]);

  const handleAgree = useCallback(async () => {
    if (typeId === null) return;
    try {
      await agree(typeId);
      toast.success(t('consent.success'));
      navigate('/settings');
    } catch {
      toast.error(t('load.error'));
    }
  }, [typeId, agree, navigate, t]);

  const title = term?.name ?? t('view.fallbackTitle');

  // Optional + consent mode + authenticated + not yet agreed → offer the opt-in.
  const showAgree =
    consentMode &&
    isAuthenticated &&
    !!term &&
    !term.required &&
    !agreed.has(term.typeId);

  return (
    <div className="termsview">
      <div className="termsview__bar">
        <button type="button" className="termsview__back" onClick={goBack}>
          ← {t('actions.back')}
        </button>
        <h1>{title}</h1>
      </div>

      {typeId === null ? (
        <p className="form-field__error">{t('load.error')}</p>
      ) : isLoading ? (
        <div aria-busy="true" className="termsview__section" />
      ) : isError || !term ? (
        <div className="termsview__section">
          <p className="form-field__error">{t('load.error')}</p>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => refetch()}
          >
            {t('load.retry')}
          </button>
        </div>
      ) : (
        <div className="termsview__section">
          <h2>
            {term.name}
            <TermBadge required={term.required} />
          </h2>
          <p className="termsview__meta">
            {t('meta.version')} {term.version} · {t('meta.effectiveDate')}{' '}
            {formatIsoToDot(term.effectiveDate)}
          </p>
          <div
            className="termsview__body"
            // BE-sanitized HTML (contract §1.2 + §3). FE renders verbatim.
            dangerouslySetInnerHTML={{ __html: term.content }}
          />

          {showAgree && (
            <button
              type="button"
              className="termsview__agree"
              onClick={handleAgree}
            >
              {t('actions.agree')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TermsViewPage;
